import { useMemo, useState, useEffect } from "react";
import {
  Banknote,
  Building2,
  CalendarRange,
  Check,
  ChevronDown,
  Clock,
  Eye,
  Landmark,
  Loader,
  RefreshCcw,
  Search,
  Timer,
  UserCheck,
  UserPlus,
  Users,
  Wallet,
  X,
} from "lucide-react";
import TablePagination from "../Utils/TablePagination";
import Logo from "../../Images/Packky.svg";
import useDriver from "../../Sevices/Driver Api/useDriver";
import useAttendance from "../../Sevices/useAttendance";

const pad2 = (num) => String(num).padStart(2, "0");

const toApiDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const formatWeekLabelDate = (date) => {
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);

  d.setDate(diff);
  d.setHours(0, 0, 0, 0);

  return d;
};

const buildSettlementWeeks = (year) => {
  const weeks = [];

  let cursor = getMonday(new Date(year, 0, 1));
  let index = 1;

  while (cursor.getFullYear() <= year || weeks.length < 53) {
    const start = new Date(cursor);
    const end = new Date(cursor);

    end.setDate(start.getDate() + 6);

    if (start.getFullYear() > year) break;

    weeks.push({
      id: `${toApiDate(start)}_${toApiDate(end)}`,
      weekNumber: index,
      fromDate: toApiDate(start),
      toDate: toApiDate(end),
      label: `Week ${index} • ${formatWeekLabelDate(start)} - ${formatWeekLabelDate(end)} ${end.getFullYear()}`,
      shortLabel: `Week ${index}`,
      display: `${toApiDate(start)} to ${toApiDate(end)}`,
      searchText:
        `week ${index} ${toApiDate(start)} ${toApiDate(end)} ${formatWeekLabelDate(start)} ${formatWeekLabelDate(end)}`.toLowerCase(),
    });

    cursor.setDate(cursor.getDate() + 7);
    index += 1;

    if (index > 54) break;
  }

  return weeks;
};

const Settlement = () => {
  const {
    settlementRows,
    settlementType,
    settlementLoading,
    fetchSettlementAmount,
  } = useDriver();
  const { fetchAttendanceApi } = useAttendance();

  const [activeTab, setActiveTab] = useState("settlement");
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [modalAttendanceData, setModalAttendanceData] = useState([]);
  const [modalAttendanceDirtyMap, setModalAttendanceDirtyMap] = useState({});
  const [modalAttendanceLoading, setModalAttendanceLoading] = useState(false);
  const settlementPerPage = 25;
  const [currentPage, setCurrentPage] = useState(1);

  const currentYear = new Date().getFullYear();

  const [settlementYear, setSettlementYear] = useState(currentYear);
  const [settlementWeekSearch, setSettlementWeekSearch] = useState("");
  const [showSettlementWeekDropdown, setShowSettlementWeekDropdown] =
    useState(false);

  const settlementWeekOptions = useMemo(() => {
    return buildSettlementWeeks(settlementYear);
  }, [settlementYear]);

  const todayMonday = getMonday(new Date());
  const todaySunday = new Date(todayMonday);
  todaySunday.setDate(todayMonday.getDate() + 6);

  const defaultSettlementWeekId = `${toApiDate(todayMonday)}_${toApiDate(
    todaySunday
  )}`;

  const [selectedSettlementWeekId, setSelectedSettlementWeekId] = useState(
    defaultSettlementWeekId
  );

  const selectedSettlementWeek = useMemo(() => {
    return (
      settlementWeekOptions.find(
        (week) => week.id === selectedSettlementWeekId
      ) || settlementWeekOptions[0]
    );
  }, [settlementWeekOptions, selectedSettlementWeekId]);

  const filteredSettlementWeekOptions = useMemo(() => {
    const q = settlementWeekSearch.trim().toLowerCase();

    if (!q) return settlementWeekOptions;

    return settlementWeekOptions.filter((week) => week.searchText.includes(q));
  }, [settlementWeekOptions, settlementWeekSearch]);

  const fromDate = selectedSettlementWeek?.fromDate || toApiDate(todayMonday);
  const toDate = selectedSettlementWeek?.toDate || toApiDate(todaySunday);

  const formatAmount = (value) => {
    const amount = Number(value || 0);

    return amount.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });
  };

  const maskAccountNo = (value) => {
    if (!value) return "-";

    const str = String(value);
    if (str.length <= 4) return str;

    return `${"•".repeat(Math.max(str.length - 4, 4))}${str.slice(-4)}`;
  };

  const getStatusMeta = (status) => {
    const normalized = String(status || "").toUpperCase();

    if (normalized === "Y") {
      return {
        label: "Settled",
        className: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      };
    }

    return {
      label: "Pending",
      className: "bg-orange-50 text-orange-700 ring-orange-200",
    };
  };

  const getDateRangeLabel = (createdOn) => {
    if (!createdOn) return "-";

    const [start, end] = String(createdOn).split(":");

    if (!start || !end) return createdOn;

    return `${start} to ${end}`;
  };

  const handleFetchSettlement = () => {
    if (!selectedSettlementWeek) return;

    fetchSettlementAmount({
      fromDate: selectedSettlementWeek.fromDate,
      toDate: selectedSettlementWeek.toDate,
    });
  };

  const handleSettlementWeekSelect = (week) => {
    setSelectedSettlementWeekId(week.id);
    setSettlementWeekSearch("");
    setShowSettlementWeekDropdown(false);
    setCurrentPage(1);

    fetchSettlementAmount({
      fromDate: week.fromDate,
      toDate: week.toDate,
    });
  };

  useEffect(() => {
    if (activeTab === "settlement" && selectedSettlementWeek) {
      fetchSettlementAmount({
        fromDate: selectedSettlementWeek.fromDate,
        toDate: selectedSettlementWeek.toDate,
        silent: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const closeDropdown = (e) => {
      if (!e.target.closest?.("[data-settlement-week-picker]")) {
        setShowSettlementWeekDropdown(false);
      }
    };

    document.addEventListener("mousedown", closeDropdown);

    return () => {
      document.removeEventListener("mousedown", closeDropdown);
    };
  }, []);

  const filteredSettlements = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return settlementRows || [];

    return (settlementRows || []).filter((item) => {
      return (
        String(item.driver_code || "").toLowerCase().includes(q) ||
        String(item.name || "").toLowerCase().includes(q) ||
        String(item.bank_name || "").toLowerCase().includes(q) ||
        String(item.bank_holder_name || "").toLowerCase().includes(q) ||
        String(item.account_no || "").toLowerCase().includes(q) ||
        String(item.ifsc_code || "").toLowerCase().includes(q)
      );
    });
  }, [settlementRows, search]);

  const totalSettlementAmount = useMemo(() => {
    return filteredSettlements.reduce(
      (sum, item) => sum + Number(item.total_amount || 0),
      0
    );
  }, [filteredSettlements]);

  const pendingCount = useMemo(() => {
    return filteredSettlements.filter(
      (item) => String(item.status || "").toUpperCase() !== "Y"
    ).length;
  }, [filteredSettlements]);

  const settledCount = useMemo(() => {
    return filteredSettlements.filter(
      (item) => String(item.status || "").toUpperCase() === "Y"
    ).length;
  }, [filteredSettlements]);

  const indexOfLast = currentPage * settlementPerPage;
  const indexOfFirst = indexOfLast - settlementPerPage;

  const currentSettlements = filteredSettlements.slice(
    indexOfFirst,
    indexOfLast
  );

  const renderPagination = () => {
    const totalCount = filteredSettlements.length;

    if (totalCount <= settlementPerPage) return null;

    return (
      <TablePagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={settlementPerPage}
        onPageChange={setCurrentPage}
      />
    );
  };
const formatDutyTime = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "-";
  }
};

const getAttendanceStatusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized.includes("full")) {
    return {
      label: "Full Day",
      className: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      dotClass: "bg-emerald-500",
    };
  }

  if (normalized.includes("half")) {
    return {
      label: "Half Day",
      className: "bg-yellow-50 text-yellow-700 ring-yellow-200",
      dotClass: "bg-yellow-500",
    };
  }

  if (normalized.includes("pending")) {
    return {
      label: "Pending",
      className: "bg-orange-50 text-orange-700 ring-orange-200",
      dotClass: "bg-orange-500",
    };
  }

  if (normalized.includes("absent")) {
    return {
      label: "Absent",
      className: "bg-red-50 text-red-700 ring-red-200",
      dotClass: "bg-red-500",
    };
  }

  return {
    label: status || "-",
    className: "bg-gray-50 text-gray-700 ring-gray-200",
    dotClass: "bg-gray-400",
  };
};

const handleViewSettlement = async (item) => {
  setSelectedSettlement(item);
  setModalAttendanceData([]);
  setModalAttendanceDirtyMap({});

  await fetchAttendanceApi(
    {
      driverCode: item.driver_code,
      fromDate,
      toDate,
    },
    setModalAttendanceData,
    setModalAttendanceDirtyMap,
    setModalAttendanceLoading
  );
};
const selectedDriverAttendance = modalAttendanceData?.[0] || null;
  return (
    <>
      <div className="min-h-screen space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center">
            <img src={Logo} alt="Logo" className="h-10" />
            <p className="ps-3 text-2xl font-bold">Settlement Report</p>
          </div>

          {activeTab === "employee" && (
            <button
              onClick={() => setEmployeeModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600"
            >
              <UserPlus size={17} />
              Add Employee
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex w-fit gap-2 rounded-2xl bg-orange-50 p-1">
          <button
            onClick={() => setActiveTab("settlement")}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${activeTab === "settlement"
                ? "bg-orange-500 text-white shadow"
                : "text-orange-700 hover:bg-white"
              }`}
          >
            Settlement
          </button>

          <button
            onClick={() => setActiveTab("employee")}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${activeTab === "employee"
                ? "bg-orange-500 text-white shadow"
                : "text-orange-700 hover:bg-white"
              }`}
          >
            <Users size={16} />
            Employee List
          </button>
        </div>

        {activeTab === "settlement" && (
          <div className="space-y-5">
            {/* Filter Card */}
            <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-100">
                    <CalendarRange size={14} />
                    Weekly Settlement Filter
                  </div>

                  <p className="mt-3 text-lg font-bold text-gray-900">
                    {settlementType || "Settlement Amount"}
                  </p>

                  <p className="mt-1 max-w-xl text-sm text-gray-500">
                    Settlement amount is calculated week-wise. Search by week
                    number or date range and apply the selected Monday to Sunday
                    period.
                  </p>
                </div>

                <div className="grid w-full grid-cols-1 gap-3 lg:w-auto lg:grid-cols-[130px_360px_280px_auto]">
                  {/* Year */}
                  <div>
                    <label
                      htmlFor="settlement-year"
                      className="mb-1 block text-xs font-semibold text-gray-500"
                    >
                      Year
                    </label>

                    <select
                      id="settlement-year"
                      value={settlementYear}
                      onChange={(e) => {
                        const year = Number(e.target.value);
                        const weeks = buildSettlementWeeks(year);
                        const firstWeek = weeks[0];

                        setSettlementYear(year);
                        setSettlementWeekSearch("");
                        setShowSettlementWeekDropdown(false);
                        setCurrentPage(1);

                        if (firstWeek) {
                          setSelectedSettlementWeekId(firstWeek.id);

                          fetchSettlementAmount({
                            fromDate: firstWeek.fromDate,
                            toDate: firstWeek.toDate,
                          });
                        }
                      }}
                      className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    >
                      {Array.from({ length: 6 }).map((_, index) => {
                        const year = currentYear - index;

                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Searchable Week Selector */}
                  <div className="relative" data-settlement-week-picker>
                    <label
                      htmlFor="settlement-week-search"
                      className="mb-1 block text-xs font-semibold text-gray-500"
                    >
                      Settlement Week
                    </label>

                    <div
                      className="relative"
                      role="combobox"
                      aria-expanded={showSettlementWeekDropdown}
                      aria-haspopup="listbox"
                      aria-controls="settlement-week-listbox"
                    >
                      <Search
                        size={16}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />

                      <input
                        id="settlement-week-search"
                        type="text"
                        value={
                          showSettlementWeekDropdown
                            ? settlementWeekSearch
                            : selectedSettlementWeek?.label || ""
                        }
                        onFocus={() => {
                          setShowSettlementWeekDropdown(true);
                          setSettlementWeekSearch("");
                        }}
                        onChange={(e) => {
                          setSettlementWeekSearch(e.target.value);
                          setShowSettlementWeekDropdown(true);
                        }}
                        placeholder="Search week number or date..."
                        className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-10 text-sm font-semibold text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          setShowSettlementWeekDropdown((prev) => !prev)
                        }
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-400 transition hover:bg-orange-50 hover:text-orange-600"
                        aria-label="Toggle settlement week dropdown"
                      >
                        <ChevronDown
                          size={18}
                          className={`transition ${showSettlementWeekDropdown ? "rotate-180" : ""
                            }`}
                        />
                      </button>
                    </div>

                    {showSettlementWeekDropdown && (
                      <div
                        id="settlement-week-listbox"
                        role="listbox"
                        className="absolute z-30 mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-orange-100 bg-white p-2 shadow-xl"
                      >
                        {filteredSettlementWeekOptions.length === 0 ? (
                          <div className="px-3 py-4 text-center text-sm text-gray-500">
                            No week found
                          </div>
                        ) : (
                          filteredSettlementWeekOptions.map((week) => {
                            const isSelected =
                              week.id === selectedSettlementWeek?.id;

                            return (
                              <button
                                key={week.id}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onClick={() =>
                                  handleSettlementWeekSelect(week)
                                }
                                className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition ${isSelected
                                    ? "bg-orange-50 text-orange-700"
                                    : "text-gray-700 hover:bg-gray-50"
                                  }`}
                              >
                                <div>
                                  <div className="text-sm font-bold">
                                    {week.shortLabel}
                                  </div>

                                  <div className="text-xs text-gray-500">
                                    {week.display}
                                  </div>
                                </div>

                                {isSelected && (
                                  <span className="rounded-full bg-orange-500 p-1 text-white">
                                    <Check size={13} />
                                  </span>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>

                  {/* Table Search */}
                  <div>
                    <label
                      htmlFor="settlement-table-search"
                      className="mb-1 block text-xs font-semibold text-gray-500"
                    >
                      Search Records
                    </label>

                    <div className="relative">
                      <Search
                        size={16}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />

                      <input
                        id="settlement-table-search"
                        type="text"
                        placeholder="Driver, bank, IFSC..."
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="h-11 w-full rounded-xl border border-gray-200 pl-9 pr-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                      />
                    </div>
                  </div>

                  {/* Apply */}
                  <button
                    type="button"
                    onClick={handleFetchSettlement}
                    disabled={settlementLoading || !selectedSettlementWeek}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {settlementLoading ? (
                      <Loader size={16} className="animate-spin" />
                    ) : (
                      <RefreshCcw size={16} />
                    )}
                    Apply Week
                  </button>
                </div>
              </div>

              {/* Selected Week Summary */}
              <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-orange-700">
                    Selected Week
                  </p>

                  <p className="mt-1 text-base font-black text-gray-900">
                    {selectedSettlementWeek?.shortLabel || "-"}
                  </p>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                    From Date
                  </p>

                  <p className="mt-1 text-base font-black text-gray-900">
                    {fromDate}
                  </p>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                    To Date
                  </p>

                  <p className="mt-1 text-base font-black text-gray-900">
                    {toDate}
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">
                      Total Amount
                    </p>

                    <p className="mt-2 text-2xl font-black text-gray-900">
                      {formatAmount(totalSettlementAmount)}
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white p-3 text-orange-500 shadow-sm">
                    <Wallet size={24} />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Drivers
                    </p>

                    <p className="mt-2 text-2xl font-black text-gray-900">
                      {filteredSettlements.length}
                    </p>
                  </div>

                  <div className="rounded-2xl bg-orange-50 p-3 text-orange-500">
                    <Users size={24} />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Pending
                    </p>

                    <p className="mt-2 text-2xl font-black text-gray-900">
                      {pendingCount}
                    </p>
                  </div>

                  <div className="rounded-2xl bg-orange-50 p-3 text-orange-500">
                    <Banknote size={24} />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Settled
                    </p>

                    <p className="mt-2 text-2xl font-black text-gray-900">
                      {settledCount}
                    </p>
                  </div>

                  <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-600">
                    <Banknote size={24} />
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-3">
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    Settlement Amount List
                  </p>

                  <p className="text-xs text-gray-500">
                    Showing {currentSettlements.length} of{" "}
                    {filteredSettlements.length} records
                  </p>
                </div>

                <div className="hidden items-center gap-2 rounded-full bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 md:flex">
                  <CalendarRange size={14} />
                  {fromDate} to {toDate}
                </div>
              </div>

              <div className="overflow-auto">
                <table className="min-w-[1100px] w-full text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-orange-500 text-white">
                    <tr>
                      <th className="px-4 py-3 text-center font-semibold">
                        S/N
                      </th>
                      <th className="px-4 py-3 font-semibold">Driver</th>
                      <th className="px-4 py-3 font-semibold">Amount</th>
                      <th className="px-4 py-3 font-semibold">
                        Bank Details
                      </th>
                      <th className="px-4 py-3 font-semibold">Account</th>
                      <th className="px-4 py-3 font-semibold">IFSC</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Period</th>
                      <th className="px-4 py-3 text-center font-semibold">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {settlementLoading ? (
                      Array.from({ length: 7 }).map((_, index) => (
                        <tr key={index} className="border-b">
                          <td className="px-4 py-4 text-center">
                            <div className="mx-auto h-4 w-6 animate-pulse rounded bg-gray-200" />
                          </td>

                          <td className="px-4 py-4">
                            <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
                            <div className="mt-2 h-3 w-20 animate-pulse rounded bg-gray-100" />
                          </td>

                          <td className="px-4 py-4">
                            <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                          </td>

                          <td className="px-4 py-4">
                            <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
                            <div className="mt-2 h-3 w-28 animate-pulse rounded bg-gray-100" />
                          </td>

                          <td className="px-4 py-4">
                            <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
                          </td>

                          <td className="px-4 py-4">
                            <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
                          </td>

                          <td className="px-4 py-4">
                            <div className="h-6 w-20 animate-pulse rounded-full bg-gray-200" />
                          </td>

                          <td className="px-4 py-4">
                            <div className="h-4 w-36 animate-pulse rounded bg-gray-200" />
                          </td>

                          <td className="px-4 py-4 text-center">
                            <div className="mx-auto h-8 w-20 animate-pulse rounded-xl bg-gray-200" />
                          </td>
                        </tr>
                      ))
                    ) : currentSettlements.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="px-4 py-14 text-center">
                          <div className="mx-auto flex max-w-sm flex-col items-center">
                            <div className="rounded-full bg-orange-50 p-4 text-orange-500">
                              <Banknote size={30} />
                            </div>

                            <p className="mt-3 text-base font-bold text-gray-900">
                              No settlement data found
                            </p>

                            <p className="mt-1 text-sm text-gray-500">
                              Try changing the week or search keyword.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      currentSettlements.map((item, index) => {
                        const statusMeta = getStatusMeta(item.status);

                        return (
                          <tr
                            key={`${item.driver_code}-${item.created_on}-${index}`}
                            className="border-b transition hover:bg-orange-50/40"
                          >
                            <td className="px-4 py-3 text-center text-gray-500">
                              {indexOfFirst + index + 1}
                            </td>

                            <td className="px-4 py-3">
                              <div className="font-bold text-gray-900">
                                {item.name || "-"}
                              </div>

                              <div className="text-xs font-semibold text-orange-600">
                                {item.driver_code || "-"}
                              </div>
                            </td>

                            <td className="px-4 py-3">
                              <div className="inline-flex items-center gap-2 rounded-xl bg-orange-50 px-3 py-1.5 font-black text-orange-700">
                                <Banknote size={15} />
                                {formatAmount(item.total_amount)}
                              </div>
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex items-start gap-2">
                                <Landmark
                                  size={17}
                                  className="mt-0.5 shrink-0 text-orange-500"
                                />

                                <div>
                                  <div className="font-semibold text-gray-900">
                                    {item.bank_name || "-"}
                                  </div>

                                  <div className="text-xs text-gray-500">
                                    {item.bank_holder_name || "-"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="px-4 py-3 font-medium text-gray-700">
                              {maskAccountNo(item.account_no)}
                            </td>

                            <td className="px-4 py-3 font-mono text-xs font-semibold text-gray-700">
                              {item.ifsc_code || "-"}
                            </td>

                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${statusMeta.className}`}
                              >
                                {statusMeta.label}
                              </span>
                            </td>

                            <td className="px-4 py-3 text-xs text-gray-600">
                              {getDateRangeLabel(item.created_on)}
                            </td>

                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleViewSettlement(item)}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-orange-200 bg-white px-3 py-1.5 text-xs font-bold text-orange-700 transition hover:bg-orange-500 hover:text-white"
                              >
                                <Eye size={14} />
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {renderPagination()}
          </div>
        )}

        {activeTab === "employee" && (
          <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
            <div className="border-b bg-orange-50 px-4 py-3">
              <p className="text-sm font-semibold text-orange-700">
                Employee List
              </p>
            </div>

            <iframe
              title="Employee List"
              src="https://billpunch.com/widget/packkyemployee/"
              className="h-[calc(100vh-230px)] min-h-[650px] w-full border-0"
            />
          </div>
        )}
      </div>

      {/* Settlement Detail Modal */}
    {selectedSettlement && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    onClick={() => setSelectedSettlement(null)}
  >
    <div
      className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b bg-orange-50 px-5 py-4">
        <div>
          <h3 className="text-base font-black text-gray-900">
            Settlement Details
          </h3>

          <p className="mt-0.5 text-xs text-gray-500">
            {selectedSettlement.name || "-"} ·{" "}
            {selectedSettlement.driver_code || "-"}
          </p>
        </div>

        <button
          onClick={() => setSelectedSettlement(null)}
          className="rounded-full p-2 text-gray-500 transition hover:bg-white hover:text-red-500"
        >
          <X size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="max-h-[calc(92vh-72px)] overflow-auto p-5">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_1fr]">
          {/* Left Settlement Summary */}
          <div className="space-y-4">
            <div className="rounded-2xl bg-orange-50 p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-orange-700">
                Settlement Amount
              </p>

              <p className="mt-2 text-3xl font-black text-gray-900">
                {formatAmount(selectedSettlement.total_amount)}
              </p>

              <div className="mt-4 inline-flex rounded-full bg-white px-3 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-100">
                {getDateRangeLabel(selectedSettlement.created_on)}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <p className="mb-3 text-sm font-black text-gray-900">
                Driver Info
              </p>

              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs font-semibold text-gray-400">
                    Driver Name
                  </p>
                  <p className="font-bold text-gray-900">
                    {selectedSettlement.name || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-400">
                    Driver Code
                  </p>
                  <p className="font-bold text-orange-600">
                    {selectedSettlement.driver_code || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-400">
                    Status
                  </p>
                  {(() => {
                    const statusMeta = getStatusMeta(selectedSettlement.status);

                    return (
                      <span
                        className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${statusMeta.className}`}
                      >
                        {statusMeta.label}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <p className="mb-3 flex items-center gap-2 text-sm font-black text-gray-900">
                <Building2 size={16} className="text-orange-500" />
                Bank Details
              </p>

              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs font-semibold text-gray-400">
                    Bank Name
                  </p>
                  <p className="font-bold text-gray-900">
                    {selectedSettlement.bank_name || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-400">
                    Account Holder
                  </p>
                  <p className="font-bold text-gray-900">
                    {selectedSettlement.bank_holder_name || "-"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-400">
                      Account No.
                    </p>
                    <p className="font-bold text-gray-900">
                      {selectedSettlement.account_no || "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-400">
                      IFSC
                    </p>
                    <p className="font-mono text-xs font-bold text-gray-900">
                      {selectedSettlement.ifsc_code || "-"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Attendance Weekly Calendar */}
          <div className="rounded-2xl border border-orange-100 bg-white">
            <div className="flex items-center justify-between border-b bg-orange-50 px-4 py-3">
              <div>
                <p className="flex items-center gap-2 text-sm font-black text-gray-900">
                  <UserCheck size={16} className="text-orange-500" />
                  Weekly Attendance
                </p>

                <p className="mt-0.5 text-xs text-gray-500">
                  {fromDate} to {toDate}
                </p>
              </div>

              {modalAttendanceLoading && (
                <Loader size={16} className="animate-spin text-orange-500" />
              )}
            </div>

            {modalAttendanceLoading ? (
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="rounded-2xl border border-gray-100 p-4"
                  >
                    <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
                    <div className="mt-3 h-3 w-40 animate-pulse rounded bg-gray-100" />
                    <div className="mt-3 h-8 w-full animate-pulse rounded bg-gray-100" />
                  </div>
                ))}
              </div>
            ) : !selectedDriverAttendance?.attendance?.length ? (
              <div className="flex min-h-[360px] items-center justify-center p-6 text-center">
                <div>
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orange-50 text-orange-500">
                    <UserCheck size={24} />
                  </div>

                  <p className="mt-3 text-sm font-bold text-gray-900">
                    No attendance found
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    No attendance record available for this selected week.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4">
                {/* Calendar Style Cards */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {selectedDriverAttendance.attendance.map((day) => {
                    const statusMeta = getAttendanceStatusMeta(day.status);

                    const formattedDate = new Date(day.date).toLocaleDateString(
                      "en-IN",
                      {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                      }
                    );

                    return (
                      <div
                        key={day.date}
                        className="rounded-2xl border border-gray-100 bg-gray-50 p-4 transition hover:border-orange-200 hover:bg-orange-50/40"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-black text-gray-900">
                              {formattedDate}
                            </p>

                            <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                              <Timer size={13} />
                              {day.totalDutyHours || "0h 0m"}
                            </p>
                          </div>

                          <span
                            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${statusMeta.className}`}
                          >
                            <span
                              className={`h-2 w-2 rounded-full ${statusMeta.dotClass}`}
                            />
                            {statusMeta.label}
                          </span>
                        </div>

                        {day.timings?.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {day.timings.map((time, idx) => (
                              <div
                                key={`${day.date}-${idx}`}
                                className="rounded-xl bg-white p-3 text-xs shadow-sm"
                              >
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <p className="text-gray-400">On Duty</p>
                                    <p className="mt-1 flex items-center gap-1 font-bold text-gray-800">
                                      <Clock size={12} />
                                      {formatDutyTime(time.on_duty_time)}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="text-gray-400">Off Duty</p>
                                    <p className="mt-1 flex items-center gap-1 font-bold text-gray-800">
                                      <Clock size={12} />
                                      {formatDutyTime(time.off_duty_time)}
                                    </p>
                                  </div>
                                </div>

                                <div className="mt-3 rounded-lg bg-orange-50 px-3 py-2">
                                  <p className="text-gray-400">Duration</p>
                                  <p className="mt-0.5 font-black text-orange-700">
                                    {time.duration || "Running / Pending"}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Small Summary Footer */}
                <div className="mt-4 rounded-2xl bg-gray-50 p-4">
                  <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
                    <div>
                      <p className="text-lg font-black text-gray-900">
                        {selectedDriverAttendance.attendance.length}
                      </p>
                      <p className="text-xs font-semibold text-gray-500">
                        Days Found
                      </p>
                    </div>

                    <div>
                      <p className="text-lg font-black text-emerald-600">
                        {
                          selectedDriverAttendance.attendance.filter((d) =>
                            String(d.status || "")
                              .toLowerCase()
                              .includes("full")
                          ).length
                        }
                      </p>
                      <p className="text-xs font-semibold text-gray-500">
                        Full Days
                      </p>
                    </div>

                    <div>
                      <p className="text-lg font-black text-orange-600">
                        {
                          selectedDriverAttendance.attendance.filter((d) =>
                            String(d.status || "")
                              .toLowerCase()
                              .includes("pending")
                          ).length
                        }
                      </p>
                      <p className="text-xs font-semibold text-gray-500">
                        Pending
                      </p>
                    </div>

                    <div>
                      <p className="text-lg font-black text-red-600">
                        {
                          selectedDriverAttendance.attendance.filter((d) =>
                            String(d.status || "")
                              .toLowerCase()
                              .includes("absent")
                          ).length
                        }
                      </p>
                      <p className="text-xs font-semibold text-gray-500">
                        Absent
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={() => setSelectedSettlement(null)}
          className="mt-5 w-full rounded-xl bg-orange-500 py-2.5 text-sm font-bold text-white transition hover:bg-orange-600"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

      {/* Add Employee Modal */}
      {employeeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative h-[90vh] w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b bg-orange-50 px-5 py-3">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Add Employee
                </h3>
                <p className="text-xs text-gray-500">
                  Fill employee details from the embedded form.
                </p>
              </div>

              <button
                onClick={() => setEmployeeModalOpen(false)}
                className="rounded-full p-2 text-gray-500 hover:bg-white hover:text-red-500"
              >
                <X size={20} />
              </button>
            </div>

            <iframe
              title="Add Employee Form"
              src="https://health.payurdue.com/employeeform"
              className="h-[calc(90vh-58px)] w-full border-0"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Settlement;